import React, {useState} from "react";
import {Checkbox, Dialog, Classes} from "@blueprintjs/core";
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";

// Components
import Button from "../common/components/button";
import TextInput from "../common/components/text-input";

// Actions
import {createClient} from "./clients-actions";

// Hooks
import useInputValidation from "../common/hooks/use-input-validation";

// Libs
import {AVAILABLE_SERVICES} from "../services/services-config";

// Redux dispatch hook
const useReduxDispatch = () => {
    const dispatch = useDispatch();
    return {
        createClient: (client) => dispatch(createClient(client))
    }
};

const NewClientDialog = (props) => {
    const {isOpen, onClose} = props;

    // i18n
    const {t} = useTranslation();

    // Redux
    const {createClient} = useReduxDispatch();

    // Form state
    const [nameData, setName] = useInputValidation();
    const [sapIdData, setSapId] = useInputValidation();
    const [nifData, setNif] = useInputValidation();
    const [services, setServices] = useState([]);

    // Reset form
    const resetForm = () => {
        setName("");
        setSapId("");
        setNif("");
        setServices([]);
    };

    const handleServiceToggle = (serviceName, checked) => {
        setServices(prev => {
            const existing = prev.find(s => s.service_name === serviceName);
            if (checked) {
                if (existing) {
                    return prev.map(s =>
                        s.service_name === serviceName ? { ...s, active: true } : s
                    );
                }
                return [...prev, {
                    service_name: serviceName,
                    active: true
                }];
            } else {
                return prev.map(s =>
                    s.service_name === serviceName ? { ...s, active: false } : s
                );
            }
        });
    };

    return (
        <Dialog
            isOpen={isOpen}
            onClose={onClose}
            lazy={true}
            style={{
                backgroundColor: "white",
                paddingBottom: "40px"
            }}
        >
            <div
                style={{margin: "20px 40px"}}
                className={Classes.DIALOG_BODY}
            >
                <h3 style={{margin: "20px 0"}}>{t("clients:newClientDialog.title")}</h3>
                <TextInput
                    label={t("clients:newClientDialog.input.name")}
                    value={nameData.value}
                    setValue={setName}
                    style={inputStyle}
                    placeholder={t("clients:newClientDialog.input.placeholder_name")}
                    validation
                    isValid={nameData.isValid}
                    errorMessage={nameData.errorMessage}
                />
                <TextInput
                    label={t("clients:newClientDialog.input.sap")}
                    value={sapIdData.value}
                    setValue={newValue => newValue.length <= 10 && setSapId(newValue)}
                    style={inputStyle}
                    placeholder={t("clients:newClientDialog.input.placeholder_sap")}
                    validation
                    isValid={sapIdData.isValid}
                    errorMessage={sapIdData.errorMessage}
                />
                <TextInput
                    label={t("clients:newClientDialog.input.nif")}
                    value={nifData.value}
                    setValue={setNif}
                    style={inputStyle}
                    placeholder={t("clients:newClientDialog.input.placeholder_nif")}
                    validation
                    isValid={nifData.isValid}
                    errorMessage={nifData.errorMessage}
                />
                <span style={{ fontSize: 14 }}>{t("clients:servicesSection.title")}</span>
                {AVAILABLE_SERVICES.map(svc => {
                    const serviceState = services.find(s => s.service_name === svc.service_name);
                    const isChecked = serviceState ? serviceState.active : false;
                    return (
                        <Checkbox
                            key={svc.service_name}
                            checked={isChecked}
                            label={t(`services:${svc.name}`)}
                            onChange={e => handleServiceToggle(svc.service_name, e.target.checked)}
                            style={{marginBottom: 8, marginTop: 8}}
                        />
                    );
                })}
            </div>
            <div
                style={{margin: "20px 40px 0 40px"}}
                className={Classes.DIALOG_FOOTER}>
                <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                    <Button
                        text={t("clients:newClientDialog.input.cancel")}
                        action={() => {
                            onClose();
                            resetForm();
                        }}
                        intent={"cancel"}
                        style={{
                            marginRight: "15px",
                        }}
                    />
                    <Button
                        text={t("clients:newClientDialog.title")}
                        action={() => {
                            createClient({
                                name: nameData.value,
                                sap_id: sapIdData.value,
                                nif: nifData.value,
                                services: services
                            });
                            onClose();
                            resetForm();
                        }}
                        intent={"secondary"}
                        disabled={!(!nameData.isValid || !sapIdData.isValid || !nifData.isValid)}
                    />
                </div>
            </div>
        </Dialog>
    );
};

export default NewClientDialog;

const inputStyle = {
    marginBottom: "15px"
};